import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase/supabase';
import { ChevronDown, ChevronUp, Activity, ArrowRight, Snowflake, Zap, MessageCircle, Plus } from 'lucide-react';
import TeamLogoImage from './league/TeamLogoImage';
import { memberDisplayName, memberFirstName } from '../utils/memberName';
import UserAvatar from './league/UserAvatar';
import { getCached, getStale, setCached, IDLE_TTL_MS } from '../utils/queryCache';
import { lastWaiverRunAt } from '../utils/season';
import { splitWaiverRivals } from '../utils/waiverRivals';
import { ordinal } from '../utils/leagueStatus';
import { useActivityReactions, REACTION_EMOJI } from '../hooks/useActivityReactions';

// A resolved bid and the move_history row it wrote share one transaction, so
// their timestamps agree. A minute of slack covers any clock detail.
const BID_MATCH_WINDOW_MS = 60 * 1000;

// move_history columns are league_id, member_id, week, team_added,
// team_dropped, created_at. This widget previously read pickedUpTeam,
// droppedTeam and managerName, none of which exist, so every entry rendered
// as "Unknown Manager made a roster move" no matter what the row said.
//
// team_added and team_dropped hold slugs (the RPCs in migrations 037/039/041
// write normalized names), so both resolve through the AuthContext teams map
// for a display name and a logo.
//
// The manager name is resolved from the members already loaded by the league
// page rather than joined here. move_history_read is is_league_member(
// league_id), and an embedded select against a helper-function policy is what
// silently returned zero rows on the home page league list, so this avoids
// the embed entirely. It also costs no second query.

const MOVES_LIMIT = 15;
// What "Show more" fetches each tap. Smaller than the first page: someone
// reading back through a season wants the next handful, and nothing older is
// downloaded until it is asked for.
const MORE_PAGE = 10;
// The Waivers view reads the season's roster adds in one request and keeps
// the ones a bid paid for. A league's whole season of adds is tens of rows,
// low hundreds at most, so a cap this size is a guard, not a page.
const WAIVER_ADDS_LIMIT = 300;
const MOVE_COLUMNS = 'id, member_id, week, team_added, team_dropped, event_type, detail, created_at';

// Attaches each waiver win's bid, and who else went for that team, to its
// move. Won bids are matched rather than joined: resolve_waivers writes a
// move_history row and marks the bid won, but nothing links the two, so they
// are paired here on member, team and time.
function decorateMoves(data, bids) {
  const wonBids = {};
  const lostByTeam = {};
  (bids || []).forEach((b) => {
    if (b.status === 'won') {
      const key = `${b.member_id}:${b.team_id}`;
      (wonBids[key] = wonBids[key] || []).push(b);
    } else {
      (lostByTeam[b.team_id] = lostByTeam[b.team_id] || []).push(b);
    }
  });

  return (data || []).map((row) => {
    // A manager can win the same team twice in a season, after dropping
    // it, so the bid is the one resolved when this move was written.
    const moveAt = new Date(row.created_at).getTime();
    const bid = (wonBids[`${row.member_id}:${row.team_added}`] || []).find((b) =>
      b.resolved_at && Math.abs(new Date(b.resolved_at).getTime() - moveAt) <= BID_MATCH_WINDOW_MS
    );
    // The other claims on the same team in the same run, split by how
    // they lost: outbid, or a later choice that came up after the team
    // was gone. See utils/waiverRivals.js. Equal amounts keep query
    // order: the standings tiebreak resolve_waivers used is not stored
    // on the bid.
    const rivals = bid
      ? splitWaiverRivals(bid, (lostByTeam[row.team_added] || [])
          .filter((b) => b.resolved_at
            && Math.abs(new Date(b.resolved_at).getTime() - new Date(bid.resolved_at).getTime()) <= BID_MATCH_WINDOW_MS))
      : { outbid: [], laterChoice: [] };
    return {
      ...row,
      // Undefined for anything that was not a waiver win, including a bid
      // of 0, which is a real amount and must stay distinguishable.
      bidAmount: bid?.amount,
      rivals,
    };
  });
}

// Same tones RecentMovesWidget's own INK/MUTED/BLUE/HAIRLINE constants hold,
// duplicated rather than threaded down as props: this is the whole reason
// ReactionBar exists as its own component and not an inline one, so it needs
// its own copy either way.
const REACTION_MUTED = 'var(--color-text-muted)';
const REACTION_BLUE = 'var(--color-brand)';
const REACTION_HAIRLINE = 'var(--color-border)';

// Reactions on one activity: the pills already in use, plus a + that reveals
// the fixed six emoji to add another.
//
// A top-level component, not an inline one defined inside RecentMovesWidget's
// body (the way TeamChip is): TeamChip holds no state, so React remounting it
// on every parent render is free. This holds a press timer and an open/closed
// picker, real state a remount would wipe every time the parent re-renders,
// which a moves-list poll or another row's reaction does constantly. Defined
// inline, every keystroke elsewhere would have silently closed an open picker
// out from under whoever was mid-tap.
function ReactionBar({ activityId, reactions, onToggle, reactorNames, isPickerOpen, onOpenPicker, onClosePicker }) {
  const barRef = useRef(null);
  const pressTimer = useRef(null);

  // Holding is the primary gesture (tapback-style, the ask this exists to
  // satisfy), but a plain click opens it too, immediately, for a mouse that
  // cannot hold anything. A click still fires after a genuine hold once the
  // pointer lifts; by then the timer has already cleared itself, so the two
  // paths never fight over which one gets to open it, they just agree.
  const startPress = () => {
    pressTimer.current = setTimeout(() => {
      pressTimer.current = null;
      onOpenPicker();
    }, 450);
  };
  const cancelPress = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  // Closes on a tap anywhere outside the bar, the same pattern TeamSlot's
  // swipe-to-cut row uses for the same reason: leaving a picker open until
  // the next explicit action is the wrong default for something covering
  // other content.
  useEffect(() => {
    if (!isPickerOpen) return undefined;
    const closeIfOutside = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) onClosePicker();
    };
    document.addEventListener('pointerdown', closeIfOutside);
    return () => document.removeEventListener('pointerdown', closeIfOutside);
  }, [isPickerOpen, onClosePicker]);

  const activeEmoji = REACTION_EMOJI.filter((emoji) => reactions[emoji]?.count > 0);

  const pillStyle = (mine) => ({
    border: `1px solid ${mine ? 'var(--color-brand)' : REACTION_HAIRLINE}`,
    backgroundColor: mine ? 'var(--color-band-bg)' : 'var(--color-bg-card)',
    borderRadius: 999,
    padding: '2px 6px',
    fontSize: 13,
    lineHeight: 1.4,
    cursor: 'pointer',
  });

  return (
    <div ref={barRef} className="mt-1.5">
      <div className="flex flex-wrap items-center gap-1">
        {activeEmoji.map((emoji) => {
          const entry = reactions[emoji];
          return (
            <button
              key={emoji}
              type="button"
              onClick={() => onToggle(activityId, emoji)}
              title={reactorNames(entry.memberIds)}
              aria-label={`React with ${emoji}${entry.mine ? ', already reacted' : ''}`}
              className="flex items-center gap-1"
              style={pillStyle(entry.mine)}
            >
              <span>{emoji}</span>
              <span className="text-[11px] font-semibold" style={{ color: entry.mine ? REACTION_BLUE : REACTION_MUTED }}>
                {entry.count}
              </span>
            </button>
          );
        })}

        {!isPickerOpen && (
          <button
            type="button"
            onClick={onOpenPicker}
            onPointerDown={startPress}
            onPointerUp={cancelPress}
            onPointerLeave={cancelPress}
            onPointerCancel={cancelPress}
            aria-label="Add a reaction"
            className="flex items-center justify-center"
            style={{
              border: `1px dashed ${REACTION_HAIRLINE}`,
              borderRadius: 999,
              width: 24,
              height: 24,
              color: REACTION_MUTED,
              backgroundColor: 'var(--color-bg-card)',
              cursor: 'pointer',
            }}
          >
            <Plus size={12} />
          </button>
        )}
      </div>

      {/* A grid in its own box below the pill row, not more pills crammed
          into that same row: two dozen do not fan out next to a + button the
          way six did, and pushing the row's own content down (rather than
          floating a popover that would need a portal to escape the feed's
          overflow-hidden card) is the same pattern TeamSlot's score
          breakdown already uses on the Lineup tab. */}
      {isPickerOpen && (
        <div
          className="flex flex-wrap gap-1 mt-1.5 p-2 rounded-xl"
          style={{ backgroundColor: 'var(--color-bg-page)', border: `1px solid ${REACTION_HAIRLINE}`, maxWidth: 260 }}
        >
          {REACTION_EMOJI.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => { onToggle(activityId, emoji); onClosePicker(); }}
              aria-label={`React with ${emoji}`}
              className="flex items-center justify-center"
              style={{
                border: `1px solid ${REACTION_HAIRLINE}`,
                backgroundColor: 'var(--color-bg-card)',
                borderRadius: 8,
                width: 28,
                height: 28,
                fontSize: 15,
                cursor: 'pointer',
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const RecentMovesWidget = ({ leagueId, memberId = null, members = [], allTeams = {}, week = null, alwaysOpen = false, readOnly = false }) => {
  // The newest page, which is what is cached and repainted on every visit.
  const [latest, setLatest] = useState([]);
  // Everything "Show more" has pulled in below it this visit. Not cached: it
  // is fetched on request, and a season's history is not worth holding on
  // every device for the few who read back through it.
  const [older, setOlder] = useState([]);
  const [exhausted, setExhausted] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Every resolved bid in the league, kept from the first fetch so older
  // waiver wins can be given their amounts without asking again. Null when
  // the first page came from the cache and the bids were never fetched.
  const bidsRef = useRef(null);
  // 'all', 'waivers' or 'plays'. Waiver wins are what the feed is opened for
  // on a Tuesday, and freezes and 5x Plays are the season's few big calls. In
  // 'all' both share fifteen rows with smack talk and cuts.
  const [filter, setFilter] = useState('all');
  // Every waiver win of the season, newest first. Null until the Waivers view
  // is first opened, so a manager who never opens it never pays for it.
  const [waiverMoves, setWaiverMoves] = useState(null);
  // Every freeze and 5x Play of the season, on the same terms. At most four
  // per manager all year, so the whole season is one small read.
  const [playMoves, setPlayMoves] = useState(null);
  // Open from the start when this is the page rather than a card on someone
  // else's page: a section reached by tapping Recent Activity has nothing to
  // collapse into.
  const [isExpanded, setIsExpanded] = useState(alwaysOpen);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueId) return;

    let cancelled = false;
    setOlder([]);
    setExhausted(false);
    setFilter('all');
    setWaiverMoves(null);
    setPlayMoves(null);
    bidsRef.current = null;

    const fetchMoves = async () => {
      // v2: rows carry rivals split by how they lost, not one runnersUp list.
      const cacheKey = `moves:v2:${leagueId}`;
      // Entries are { rows, fetchedAt }. A bare array is a copy written before
      // fetchedAt existed, treated as too old to trust.
      //
      // A copy fetched before the last 6am Tuesday run is refetched even inside
      // its TTL. The run happens on the server, so nothing in the browser can
      // invalidate this key when it writes the week's waiver wins.
      const cached = getCached(cacheKey);
      const fresh = cached && !Array.isArray(cached)
        && cached.fetchedAt >= lastWaiverRunAt().getTime();
      if (fresh) {
        setLatest(cached.rows);
        setLoading(false);
        return;
      }

      // Past the TTL, the last copy is still worth painting while the fetch
      // runs. move_history is append-only and a resolved bid never changes
      // its amount, so a stale entry is short rather than wrong: the list
      // fills in immediately on a cold launch and grows a row when the
      // response lands.
      const stale = getStale(cacheKey);
      const staleRows = Array.isArray(stale) ? stale : stale?.rows;
      if (staleRows) {
        setLatest(staleRows);
        setLoading(false);
      }

      try {
        // Won bids are read alongside the moves rather than joined onto them.
        // resolve_waivers writes a move_history row and marks the bid won, but
        // nothing links the two, so they are matched here on member, team and
        // time. fa_bids has no week column: this used to select one, the query
        // failed, the error was never checked, and every waiver win read as a
        // plain "Added a team". fa_bids_read_league exposes won and lost bids
        // to the whole league by design, so the amount is publishable once
        // resolved.
        const [{ data, error }, { data: bids, error: bidsError }] = await Promise.all([
          supabase
            .from('move_history')
            .select(MOVE_COLUMNS)
            .eq('league_id', leagueId)
            .order('created_at', { ascending: false })
            // A waiver run writes several rows in one transaction, all with
            // the same timestamp. The id settles their order, so "Show more"
            // pages the same sequence this query started.
            .order('id', { ascending: false })
            .limit(MOVES_LIMIT),
          supabase
            .from('fa_bids')
            .select('member_id, team_id, amount, claim_rank, status, resolved_at')
            .eq('league_id', leagueId)
            .in('status', ['won', 'lost']),
        ]);

        if (error) throw error;
        // Not fatal: the moves still render, only without bid amounts. Logged,
        // because swallowing this is how the missing week column went unseen.
        if (bidsError) console.error('Error fetching won bids:', bidsError);
        if (cancelled) return;

        bidsRef.current = bids || [];
        const rows = decorateMoves(data, bids);
        setLatest(rows);
        // Roster moves do not change while a game is playing, so they take the
        // idle interval. Every path that writes one (waiver resolution,
        // instant add, a cut) calls invalidateLeague, which sweeps this key,
        // on disk as well as in memory.
        //
        // Persisted because this is the one league query whose answer is a
        // log: nothing already in it can change, so yesterday's copy is a
        // correct prefix of today's rather than a wrong version of it.
        setCached(cacheKey, { rows, fetchedAt: Date.now() }, IDLE_TTL_MS, { persist: true });
      } catch (error) {
        console.error('Error fetching moves:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMoves();

    // There is deliberately no realtime subscription here. The
    // supabase_realtime publication contains no tables, so the channel this
    // component used to open received nothing at all. Moves are written by a
    // manager acting in the app, and that write invalidates the cache.
    return () => { cancelled = true; };
  }, [leagueId]);

  // Deduped by id: a move made since the first page loaded pushes every row
  // down one, so the next page can begin with a row already on screen.
  const moves = React.useMemo(() => {
    const seen = new Set(latest.map((m) => m.id));
    return [...latest, ...older.filter((m) => !seen.has(m.id) && seen.add(m.id))];
  }, [latest, older]);
  // A first page that came back short is the whole history.
  const hasMore = !exhausted && latest.length >= MOVES_LIMIT;

  const ensureBids = async () => {
    if (bidsRef.current) return;
    const { data: bids, error: bidsError } = await supabase
      .from('fa_bids')
      .select('member_id, team_id, amount, claim_rank, status, resolved_at')
      .eq('league_id', leagueId)
      .in('status', ['won', 'lost']);
    if (bidsError) console.error('Error fetching won bids:', bidsError);
    bidsRef.current = bids || [];
  };

  // The Waivers view. A waiver win is not marked as one in move_history: it
  // is an ordinary add that a won bid happens to match. So the season's adds
  // are read once and the matched ones kept, rather than paging through the
  // mixed feed looking for the next one.
  const showWaivers = async () => {
    setFilter('waivers');
    if (waiverMoves) return;
    try {
      await ensureBids();
      const { data, error } = await supabase
        .from('move_history')
        .select(MOVE_COLUMNS)
        .eq('league_id', leagueId)
        .eq('event_type', 'move')
        .not('team_added', 'is', null)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(WAIVER_ADDS_LIMIT);
      if (error) throw error;
      setWaiverMoves(decorateMoves(data, bidsRef.current).filter((m) => m.bidAmount != null));
    } catch (error) {
      console.error('Error fetching waiver wins:', error);
      setWaiverMoves([]);
    }
  };

  // The Plays view: freezes and 5x Plays. These are marked by event_type, so
  // unlike waiver wins the database can pick them out itself.
  const showPlays = async () => {
    setFilter('plays');
    if (playMoves) return;
    try {
      const { data, error } = await supabase
        .from('move_history')
        .select(MOVE_COLUMNS)
        .eq('league_id', leagueId)
        .in('event_type', ['freeze', 'trip_play'])
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(WAIVER_ADDS_LIMIT);
      if (error) throw error;
      setPlayMoves(decorateMoves(data, []));
    } catch (error) {
      console.error('Error fetching freezes and 5x Plays:', error);
      setPlayMoves([]);
    }
  };

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      await ensureBids();
      // One row past the page, to know whether there is anything after it
      // without a second request.
      const from = latest.length + older.length;
      const { data, error } = await supabase
        .from('move_history')
        .select(MOVE_COLUMNS)
        .eq('league_id', leagueId)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, from + MORE_PAGE);
      if (error) throw error;
      const rows = data || [];
      setOlder((cur) => [...cur, ...decorateMoves(rows.slice(0, MORE_PAGE), bidsRef.current)]);
      if (rows.length <= MORE_PAGE) setExhausted(true);
    } catch (error) {
      console.error('Error fetching older moves:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const memberById = React.useMemo(() => {
    const map = {};
    (members || []).forEach((m) => { map[m.id] = m; });
    return map;
  }, [members]);

  // What the list is drawing: the mixed feed, or the season's waiver wins.
  const shown = React.useMemo(
    () => (filter === 'waivers' ? (waiverMoves || []) : filter === 'plays' ? (playMoves || []) : moves),
    [filter, waiverMoves, playMoves, moves]
  );
  const activityIds = React.useMemo(() => shown.map((m) => m.id), [shown]);
  const { reactionsByActivity, toggle: toggleReaction } = useActivityReactions({ leagueId, memberId, activityIds });
  const [reactionError, setReactionError] = useState('');
  const handleToggleReaction = async (activityId, emoji) => {
    const error = await toggleReaction(activityId, emoji);
    setReactionError(error || '');
  };
  const reactorNames = (memberIds) => memberIds.map((id) => memberDisplayName(memberById[id])).join(', ');
  // Only one add-reaction picker open at a time, across the whole feed:
  // opening a second one implicitly closes the first, rather than leaving a
  // row's picker stuck open under someone else's finger.
  const [pickerOpenFor, setPickerOpenFor] = useState(null);

  const formatTimeAgo = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  // A slug that is not in the teams map still renders: the slug is shown as
  // written rather than the row being dropped, so a team leaving FBS does not
  // erase a manager's move from the record.
  const teamLabel = (slug) => allTeams?.[slug]?.school || slug;

  // One line saying what happened, above the team chips that say to whom.
  // A waiver win is distinguished from an ordinary pickup because it is the
  // more interesting event: it was contested, and the amount is the story.
  const describe = (move) => {
    if (move.event_type === 'freeze') {
      const points = move.detail?.points;
      return points != null ? `Froze a team on ${points} points` : 'Used a freeze';
    }
    if (move.event_type === 'trip_play') {
      return 'Played their 5x';
    }
    if (move.event_type === 'smack_talk') {
      return 'Changed their smack talk';
    }
    // No "cut a team" on the end: the red chip below already names the team
    // that went, and nearly every waiver win has one.
    if (move.bidAmount != null) return `Won on waivers for $${move.bidAmount}`;
    if (move.team_added && move.team_dropped) return 'Swapped a team';
    if (move.team_added) return 'Added a team';
    if (move.team_dropped) return 'Cut a team';
    return 'Made a roster move';
  };

  // Same palette as the standings table this sits beside: ink, muted grey,
  // the brand blue for the one accent, and the standings' own zebra pair.
  const INK = 'var(--color-text-strong)';
  const MUTED = 'var(--color-text-muted)';
  const BLUE = 'var(--color-brand)';
  const HAIRLINE = 'var(--color-border)';

  const CHIP_TONES = {
    in: { bg: 'var(--color-success-bg)', border: 'var(--color-success-border)', text: 'var(--color-success-text)' },
    out: { bg: 'var(--color-danger-bg)', border: 'var(--color-danger-border)', text: 'var(--color-danger-text)' },
    neutral: { bg: 'var(--color-bg-page)', border: 'var(--color-border)', text: 'var(--color-text-muted)' },
  };

  const TeamChip = ({ slug, tone }) => (
    <span
      className="inline-flex items-center gap-1.5 rounded-full pl-1 pr-2.5 py-1 max-w-full"
      style={{
        backgroundColor: (CHIP_TONES[tone] || CHIP_TONES.neutral).bg,
        border: `1px solid ${(CHIP_TONES[tone] || CHIP_TONES.neutral).border}`,
      }}
    >
      <TeamLogoImage
        teamName={allTeams?.[slug]?.school || slug}
        primaryColor={allTeams?.[slug]?.colors?.primary}
        size={18}
      />
      <span
        className="text-[12px] font-semibold truncate"
        style={{ color: (CHIP_TONES[tone] || CHIP_TONES.neutral).text }}
      >
        {teamLabel(slug)}
      </span>
    </span>
  );

  if (loading) return null;

  const countLabel = `${moves.length} ${moves.length === 1 ? 'update' : 'updates'}`;

  // All, Waivers or Plays. Same segmented control as Standings / Playoffs on
  // this tab. Not shown on an empty feed, where there is nothing to filter.
  // "Plays" is the app's own word for both: the 5x Play, and "Freeze plays"
  // on the Lineup tab's sheet.
  const filterBar = moves.length > 0 && (
    <div className="px-3 pt-3 pb-1">
      <div className="flex rounded-full p-1" style={{ backgroundColor: HAIRLINE }} role="tablist">
        {[
          { key: 'all', label: 'All', onSelect: () => setFilter('all') },
          { key: 'waivers', label: 'Waivers', onSelect: showWaivers },
          { key: 'plays', label: 'Plays', onSelect: showPlays },
        ].map(({ key, label, onSelect }) => {
          const active = filter === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={onSelect}
              className="flex-1 rounded-full py-1.5 text-[13px] font-semibold transition-colors"
              style={{
                backgroundColor: active ? 'var(--color-bg-card)' : 'transparent',
                color: active ? INK : MUTED,
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const list = shown.length === 0 ? (
    <div className="px-6 py-10 text-center">
      <p className="text-sm" style={{ color: MUTED }}>
        {filter === 'waivers'
          ? (waiverMoves === null
            ? 'Loading waiver results…'
            : 'No waiver wins yet. Results appear here after each 6am Tuesday run.')
          : filter === 'plays'
            ? (playMoves === null
              ? 'Loading freezes and 5x Plays…'
              : 'Nobody has used a freeze or their 5x Play yet.')
            : 'Pickups, cuts, waiver wins, freezes and 5x Plays appear here as managers make them.'}
      </p>
    </div>
  ) : (
    // A hairline divider between rows, not zebra striping: row heights vary a
    // lot by event type (a smack-talk row's quote box versus a bare "Played
    // their 5x"), so the striped grid this matched on the standings table
    // never actually lined up here, and #FFFFFF next to #FAFAFB is too close
    // to read as separation on its own.
    <div className={alwaysOpen ? undefined : 'max-h-80 overflow-y-auto'}>
      {shown.map((move, idx) => {
        const member = memberById[move.member_id];
        const managerName = memberDisplayName(member);

        // Blocking masks a member's smack talk everywhere else (maskBlockedMember
        // in src/utils/blocking.js nulls it), so the feed must not be the one
        // place their words still reach you. The event carries its own copy of
        // the text, so it has to be dropped here rather than relying on the
        // masked member. A roster move by a blocked manager still shows: that
        // is league record, not their content.
        if (move.event_type === 'smack_talk' && member?.blocked) return null;

        return (
          <div
            key={move.id}
            className="flex items-start gap-2.5 px-3 py-3"
            style={{ borderBottom: idx < shown.length - 1 ? `1px solid ${HAIRLINE}` : undefined }}
          >
            {member
              ? <UserAvatar member={member} size={32} />
              : <div className="w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-page)' }} />}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold truncate" style={{ color: INK }}>
                  {managerName}
                </span>
                {move.event_type === 'freeze' && (
                  <Snowflake size={12} className="flex-shrink-0" style={{ color: 'var(--color-freeze)' }} />
                )}
                {move.event_type === 'trip_play' && (
                  <Zap size={12} className="flex-shrink-0" style={{ color: 'var(--color-tripplay-text)' }} />
                )}
                {move.event_type === 'smack_talk' && (
                  <MessageCircle size={12} className="flex-shrink-0" style={{ color: BLUE }} />
                )}
                {/* Time and week are the same kind of fact, so they share one
                    slot at the end of the name line rather than the week
                    getting a line of its own under the chips. */}
                <span className="text-[11px] flex-shrink-0 ml-auto" style={{ color: MUTED }}>
                  {formatTimeAgo(move.created_at)}
                  {move.week != null ? ` · Wk ${move.week}` : ''}
                </span>
              </div>

              <div className="text-[12px] mt-0.5" style={{ color: MUTED }}>
                {describe(move)}
              </div>

              {/* Who else wanted the team, at most two of each kind. "Outbid"
                  is only the claims this one was actually compared with. A
                  claim that was someone's later choice gets its own line and
                  says which choice it was, because it can carry a bigger
                  number than the win above it and would otherwise read as a
                  mistake. Nothing when it was not contested. */}
              {move.rivals?.outbid?.length > 0 && (
                <div className="text-[12px] mt-0.5" style={{ color: MUTED, overflowWrap: 'anywhere' }}>
                  {'Outbid: '}
                  {/* First names here, not squad names: these lines are about
                      what a person did, and the row's own heading is the only
                      place the squad needs naming. */}
                  {move.rivals.outbid.map((r, i) => (
                    <span key={`${r.memberId}:${i}`}>
                      {i > 0 && ', '}
                      <span style={{ color: INK }}>{memberFirstName(memberById[r.memberId])}</span>
                      {` ($${r.amount})`}
                    </span>
                  ))}
                </div>
              )}
              {move.rivals?.laterChoice?.length > 0 && (
                <div className="text-[12px] mt-0.5" style={{ color: MUTED, overflowWrap: 'anywhere' }}>
                  {'Also claimed by: '}
                  {move.rivals.laterChoice.map((r, i) => (
                    <span key={`${r.memberId}:${i}`}>
                      {i > 0 && ', '}
                      <span style={{ color: INK }}>{memberFirstName(memberById[r.memberId])}</span>
                      {` as their ${ordinal(r.rank)} choice ($${r.amount})`}
                    </span>
                  ))}
                </div>
              )}

              {/* This event's own copy, not the member's current smack talk.
                  A manager who changed their line three times this season
                  wrote three of these rows, one per week, each with its own
                  text in detail.text at the time. Reading member.smack_talk
                  instead showed the same current line on all three: every
                  past change read as a duplicate of whatever was live right
                  now. Blocking is handled entirely by the early return above
                  (`member?.blocked`), which drops the whole row before this
                  ever renders, so there is nothing left for the live column
                  to mask here.

                  overflowWrap anywhere because smack talk is 80 characters of
                  whatever someone types, and an unbroken run of them offers
                  nowhere to break. */}
              {move.event_type === 'smack_talk' && move.detail?.text && (
                <div
                  className="text-[13px] mt-1.5 rounded-lg px-2.5 py-1.5"
                  style={{
                    color: INK,
                    backgroundColor: 'var(--color-bg-page)',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {move.detail.text}
                </div>
              )}

              {/* Wraps rather than scrolls: two team names plus an arrow does
                  not fit on one line at 390px. */}
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                {move.team_dropped && <TeamChip slug={move.team_dropped} tone="out" />}
                {move.team_added && move.team_dropped && (
                  <ArrowRight size={13} style={{ color: 'var(--color-text-faint)' }} />
                )}
                {move.team_added && (
                  <TeamChip
                    slug={move.team_added}
                    tone={move.event_type === 'move' ? 'in' : 'neutral'}
                  />
                )}
              </div>

              {/* Reactions already on this activity show as pills; a new one
                  is added by holding (or clicking, for a mouse) the + at the
                  end, which reveals the fixed six in place. See ReactionBar
                  below for why it has to be its own top-level component. */}
              {/* Not in the public demo league, where a visitor cannot
                  write a reaction and the demo managers never leave one. */}
              {!readOnly && <ReactionBar
                activityId={move.id}
                reactions={reactionsByActivity[move.id] || {}}
                onToggle={handleToggleReaction}
                reactorNames={reactorNames}
                isPickerOpen={pickerOpenFor === move.id}
                onOpenPicker={() => setPickerOpenFor(move.id)}
                onClosePicker={() => setPickerOpenFor((cur) => (cur === move.id ? null : cur))}
              />}
            </div>
          </div>
        );
      })}
      {reactionError && (
        <p className="text-xs px-3 py-2" style={{ color: 'var(--color-danger-text)' }}>{reactionError}</p>
      )}
      {/* Back through the whole season, ten at a time. Gone once the last
          page comes back short, rather than left to load nothing. */}
      {filter === 'all' && hasMore && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          className="w-full flex items-center justify-center gap-1 text-[13px] font-semibold"
          style={{ minHeight: 44, color: BLUE, borderTop: `1px solid ${HAIRLINE}`, background: 'none', opacity: loadingMore ? 0.6 : 1 }}
        >
          {loadingMore ? 'Loading…' : 'Show more'}
          {!loadingMore && <ChevronDown size={14} />}
        </button>
      )}
    </div>
  );

  // As a section it owns the page column and titles itself the way the
  // standings section does: a plain label on the grey, then the card. As a
  // card it carries its own collapsing header instead.
  if (alwaysOpen) {
    return (
      <div className="max-w-4xl mx-auto sm:px-6 pt-3 pb-28">
        <div className="flex items-center gap-2 mb-3 px-4 sm:px-1">
          <span className="text-sm font-bold" style={{ color: INK }}>
            Recent activity
          </span>
          <span style={{ color: 'var(--color-text-faint)' }}>·</span>
          <span className="text-sm" style={{ color: MUTED }}>
            {week ? `Week ${week}` : countLabel}
          </span>
        </div>

        <div
          className="sm:rounded-2xl border-y sm:border shadow-sm overflow-hidden"
          style={{ backgroundColor: 'var(--color-bg-card)', borderColor: HAIRLINE }}
        >
          {filterBar}
          {list}
        </div>
      </div>
    );
  }

  return (
    <div
      className="sm:rounded-2xl border-y sm:border mb-6 overflow-hidden shadow-sm"
      style={{ backgroundColor: 'var(--color-bg-card)', borderColor: HAIRLINE }}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-4 flex items-center justify-between"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--color-band-bg)' }}>
            <Activity size={16} style={{ color: BLUE }} />
          </div>
          <h3 className="text-sm font-bold truncate" style={{ color: INK }}>
            Recent activity
          </h3>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[12px]" style={{ color: MUTED }}>
            {moves.length === 0 ? 'Nothing yet' : countLabel}
          </span>
          {isExpanded
            ? <ChevronUp size={18} style={{ color: MUTED }} />
            : <ChevronDown size={18} style={{ color: MUTED }} />}
        </div>
      </button>

      {isExpanded && <div style={{ borderTop: `1px solid ${HAIRLINE}` }}>{filterBar}{list}</div>}
    </div>
  );
};

export default RecentMovesWidget;
